import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BTN_LG } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiPost } from '../utils/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    apiPost('/api/auth/verify-email', { token })
      .then(async (res) => {
        const data = await res.json();
        // Confirmed from a different browser than the signup: the server
        // discarded the password typed there, so choose one on set-password.
        if (res.ok && data.set_password_token) {
          navigate(`/set-password?token=${encodeURIComponent(data.set_password_token)}`, { replace: true });
          return;
        }
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified!');
        } else {
          setStatus('error');
          setMessage(data.error || 'Verification failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Network error. Please try again.');
      });
  }, [token, navigate]);

  const handleResend = async () => {
    // We don't know the email from the token, so prompt isn't ideal.
    // The user should go back to login and use the resend from there.
    setResending(true);
    // This is a fallback — ideally resend is triggered from login page
    setResent(true);
    setResending(false);
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Verifying your email..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          {status === 'success' ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email verified</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
              <Link to="/login" className={`${BTN_LG.primary} inline-flex justify-center`}>
                Sign in
              </Link>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Verification failed</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Try logging in — you can request a new verification email from the login page.
              </p>
              <Link to="/login" className={`${BTN_LG.primary} inline-flex justify-center`}>
                Go to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
